import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Menu } from "antd";
import type { MenuProps } from "antd";

import { AppDispatch } from "../../store";
import { spaceListSelector } from "../../store/space/list";
import {
  addDashboardTabList,
  dashboardSelector,
  removeDashboardTab,
  renameDashboardTab,
  updateCurrentDashboard,
  updateDashboardTabList,
} from "../../store/dashboard/main";
import {
  dashboardListSelector,
  fetchGetDashboardList,
} from "../../store/dashboard-manage/list";
import { fetchGetSpaceList } from "../../store/space/list";
import {
  removeSpaceTab,
  renameSpaceTab,
  spaceDetailSelector,
  updateCurrentSpaceId,
  updateSpaceTabList,
} from "../../store/space/detail";
import { updateOpenSettingModal, settingSelector } from "../../store/setting";
import HoverSetting from "./HoverSetting";
import "./navigation.scss";
import { layoutSelector, resetLayout } from "../../store/layout";
import {
  dashboardRegisterSelector,
  resetDashboardRegister,
} from "../../store/dashboard/register";
import {
  dashboardRenameSelector,
  resetDashboardRename,
} from "../../store/dashboard/rename";
import {
  dashboardDeleteSelector,
  resetDashboardDelete,
} from "../../store/dashboard/delete";
import { createSpaceSelector } from "../../store/space/register";
import {
  updateSpaceSelector,
  resetUpdateSpace,
} from "../../store/space/update";
import {
  resetSpaceRemove,
  removeSpaceSelector,
} from "../../store/space/remove";

type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  className?: string
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    className,
  } as MenuItem;
}

function NavigationMenus() {
  const [currentMenu, setCurrentMenu] = useState("");
  const [current, setCurrent] = useState("");
  const [selectedMenu, setSelectedMenu] = useState("");
  const token = localStorage.getItem("accessToken");
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch<AppDispatch>();
  const { openSettingModal } = useSelector(settingSelector);
  const { dashboardList } = useSelector(dashboardListSelector);
  const { dashboardTabList, currentDashboard } = useSelector(dashboardSelector);
  const { spaceList } = useSelector(spaceListSelector);
  const {
    success: dashboardRegisterSuccess,
    id: dashboardInsertId,
    dashboardName,
  } = useSelector(dashboardRegisterSelector);
  const { spaceTabList, currentSpaceId } = useSelector(spaceDetailSelector);
  const {
    id: dashboardRenameId,
    success: dashboardRenameSuccess,
    dashboardName: renamedDashboardName,
  } = useSelector(dashboardRenameSelector);
  const { id: dashboardDeleteId, success: dashboardDeleteSuccess } =
    useSelector(dashboardDeleteSelector);
  const { menu, id: dashboardId } = useSelector(layoutSelector);
  const { success: createSpaceSuccess, name: createName } =
    useSelector(createSpaceSelector);
  const {
    success: updateSpaceSuccess,
    modal: { updateSpaceId, name: updateName, address },
  } = useSelector(updateSpaceSelector);
  const { success: removeSpaceSuccess, spaceId } =
    useSelector(removeSpaceSelector);
  const { loading } = useSelector(dashboardSelector);
  const [disabledTimer, setDisabledTimer] = useState(false);

  useEffect(() => {
    setDisabledTimer(true);
    const timer = setTimeout(() => {
      setDisabledTimer(false);
    }, 2000);
    if (!loading) return undefined;
    return () => clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    if (!!token) {
      dispatch(fetchGetDashboardList());
      dispatch(fetchGetSpaceList());
    }
  }, [token]);

  useEffect(() => {
    if (dashboardRegisterSuccess && dashboardInsertId && dashboardName) {
      dispatch(resetLayout());
      dispatch(fetchGetDashboardList());
      dispatch(addDashboardTabList({ id: dashboardInsertId, dashboardName }));
      dispatch(updateCurrentDashboard(dashboardInsertId));
      setCurrent(dashboardInsertId.toString());
      dispatch(resetDashboardRegister());
    } else if (dashboardRenameSuccess && dashboardRenameId) {
      dispatch(resetLayout());
      dispatch(fetchGetDashboardList());
      dispatch(
        renameDashboardTab({
          id: dashboardRenameId,
          name: renamedDashboardName,
        })
      );
      dispatch(resetDashboardRename());
    } else if (dashboardDeleteSuccess) {
      dispatch(resetLayout());
      dispatch(fetchGetDashboardList());
      dispatch(removeDashboardTab(dashboardDeleteId));
      dispatch(updateCurrentDashboard(1));
      dispatch(resetDashboardDelete());
    } else if (createSpaceSuccess) {
      dispatch(fetchGetSpaceList());
    } else if (updateSpaceSuccess) {
      dispatch(fetchGetSpaceList());
      if (spaceTabList.length > 0)
        dispatch(
          renameSpaceTab({
            id: updateSpaceId,
            name: updateName,
            address,
          })
        );
      dispatch(resetUpdateSpace());
    } else if (removeSpaceSuccess) {
      dispatch(fetchGetSpaceList());
      dispatch(removeSpaceTab(spaceId));
      dispatch(resetSpaceRemove());
    }
  }, [
    dashboardRenameSuccess,
    dashboardDeleteSuccess,
    dashboardRegisterSuccess,
    dashboardInsertId,
    dashboardRenameId,
    renamedDashboardName,
    dashboardDeleteId,
    dashboardName,
    updateSpaceSuccess,
    createSpaceSuccess,
    spaceTabList,
    updateSpaceId,
    updateName,
    createName,
    removeSpaceSuccess,
  ]);

  useEffect(() => {
    if (location.pathname.split("/").length < 3) {
      setCurrent("");
    }
  }, [location, location.pathname]);

  useEffect(() => {
    if (location.pathname.split("/")[1] === selectedMenu) {
      setCurrentMenu(selectedMenu);
      setSelectedMenu("");
    }
  }, [
    selectedMenu,
    currentMenu,
    selectedMenu.length,
    currentMenu.length,
    location.pathname,
    location,
  ]);

  useEffect(() => {
    !openSettingModal && setCurrent("");
  }, [openSettingModal]);

  useEffect(() => {
    currentDashboard && setCurrent(`dashboard-${currentDashboard}`);
  }, [currentDashboard]);

  useEffect(() => {
    currentSpaceId &&
      setCurrent(`${location.pathname.split("/")[1]}-${currentSpaceId}`);
  }, [currentSpaceId]);

  useEffect(() => {
    if (!!current && (spaceTabList.length > 0 || dashboardTabList.length > 0)) {
      const id = Number(current.split("-")[1]);
      if (
        !(
          !!spaceTabList.find(
            (element: { id: string }) => Number(element.id) === id
          ) ||
          !!dashboardTabList.find(
            (element: { id: number }) => element.id === id
          )
        )
      ) {
        setCurrent("");
      }
    }
  }, [dashboardTabList, spaceTabList]);

  useEffect(() => {
    const path =
      location.pathname.split("/")[1].indexOf("space") !== -1
        ? "space"
        : location.pathname.split("/")[1];
    current.indexOf(path) === -1 && setCurrent("");
  }, [location.pathname, current]);

  const setOpenSettingModal = useCallback(() => {
    dispatch(updateOpenSettingModal(!openSettingModal));
  }, [openSettingModal]);

  useEffect(() => {
    if (currentMenu) {
    }
  }, [currentMenu]);

  const onClickLink = (value: string) => () => {
    setSelectedMenu(value);
  };

  const onSelectMenu = (e: any) => {
    setCurrent(e.keyPath[0]);
    const id = Number(e.key.split("-")[1]);
    const space = spaceList.filter(
      (item: { id: number; name: string }) => item.id === id
    );
    let newMenus = [];

    if (e.keyPath[1] === "dashboard" && e.key.split("-")[1] !== "add") {
      navigate("/dashboard");
      const dashboard = dashboardList.filter((item) => item.id === id);

      newMenus.push(...dashboardTabList, dashboard[0]);

      const newMenuValues = newMenus;
      const newTabs = newMenus.filter((newMenu, index) => {
        return (
          newMenuValues.findIndex((newMenuValue) => {
            return newMenu.id === newMenuValue.id;
          }) === index
        );
      });

      dispatch(updateDashboardTabList(newTabs));
      dispatch(updateCurrentDashboard(dashboard[0].id));
    } else if (e.keyPath[1] === "space") {
      navigate(`/space/detail`);
      const id = Number(e.key.slice(6));
      newMenus.push(...spaceTabList, space[0]);

      const NewMenuValues = newMenus;
      const newTabs = newMenus.filter((NewMenuValue, index) => {
        return (
          NewMenuValues.findIndex((item2) => {
            return NewMenuValue.id === item2.id;
          }) === index
        );
      });

      dispatch(updateCurrentSpaceId(id));
      dispatch(updateSpaceTabList(newTabs));
    } else if (e.keyPath[1] === "cctv") {
      navigate(`/cctv/detail`);

      newMenus.push(...spaceTabList, space[0]);

      const newMenuValues = newMenus;
      let newTabs = newMenuValues.filter((newMenuValue, index) => {
        return (
          newMenuValues.findIndex((item2) => {
            return newMenuValue.id === item2.id;
          }) === index
        );
      });

      dispatch(updateCurrentSpaceId(id));
      dispatch(updateSpaceTabList(newTabs));
    } else if (e.keyPath[1] === "sellManage") {
      navigate(`/sellManage/detail`);

      newMenus.push(...spaceTabList, space[0]);

      const newMenuValues = newMenus;
      let newTabs = newMenuValues.filter((newMenuValue, index) => {
        return (
          newMenuValues.findIndex((item2) => {
            return newMenuValue.id === item2.id;
          }) === index
        );
      });

      dispatch(updateCurrentSpaceId(id));
      dispatch(updateSpaceTabList(newTabs));
    } else if (e.keyPath[0] === "setting") {
      setOpenSettingModal();
    } else {
      setCurrentMenu("");
    }
  };

  const onOpenChange = (openKeys: string[]) => {
    if (openKeys.length > 0) {
      const newKey = openKeys.filter((menu) => menu !== currentMenu)[0];
      setCurrentMenu(newKey);
    } else if (location.pathname.split("/")[1] === selectedMenu) {
    } else {
      setCurrentMenu("");
    }
  };

  const items: MenuItem[] = useMemo(() => {
    return [
      getItem(
        <img
          src={`${process.env.REACT_APP_S3_URL}/1.0/button-icons/hejhome_logo.png`}
          alt="hejhome_logo"
        />,
        "icon"
      ),
      getItem(
        <NavLink to="/dashboard" onClick={onClickLink("dashboard")}>
          대시보드
        </NavLink>,
        "dashboard",
        <svg
          width={20}
          height={20}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.63.833h.74c1.369 0 2.447 0 3.315.071.885.073 1.625.223 2.296.565a5.834 5.834 0 0 1 2.55 2.55c.342.671.492 1.41.565 2.296.07.868.07 1.946.07 3.315v.74c0 1.37 0 2.448-.07 3.315-.073.886-.223 1.625-.565 2.297a5.833 5.833 0 0 1-2.55 2.549c-.671.342-1.41.493-2.296.565-.868.07-1.946.07-3.315.07h-.74c-1.37 0-2.448 0-3.315-.07-.886-.072-1.625-.223-2.297-.565a5.833 5.833 0 0 1-2.549-2.55c-.342-.671-.493-1.41-.565-2.296-.07-.867-.07-1.946-.07-3.315v-.74c0-1.369 0-2.447.07-3.315.072-.885.223-1.625.565-2.296a5.833 5.833 0 0 1 2.55-2.55c.671-.342 1.41-.492 2.296-.565.867-.07 1.946-.07 3.315-.07ZM6.45 2.566c-.782.063-1.277.186-1.675.388a4.167 4.167 0 0 0-1.82 1.821c-.204.398-.326.893-.39 1.676-.064.792-.065 1.802-.065 3.216v.667c0 1.413 0 2.424.065 3.216.064.782.186 1.277.39 1.675a4.167 4.167 0 0 0 1.82 1.821c.398.203.893.325 1.675.389.792.065 1.803.065 3.217.065h.666c1.414 0 2.424 0 3.216-.065.783-.064 1.278-.186 1.676-.389a4.167 4.167 0 0 0 1.82-1.82c.203-.399.326-.894.39-1.676.064-.792.065-1.803.065-3.216v-.667c0-1.414 0-2.424-.065-3.216-.064-.783-.187-1.278-.39-1.676a4.167 4.167 0 0 0-1.82-1.82c-.398-.203-.893-.326-1.676-.39-.792-.064-1.802-.065-3.216-.065h-.666c-1.414 0-2.425 0-3.217.066Zm-.143 1.6H7.027c.21 0 .415 0 .588.015.19.016.415.052.642.167.313.16.568.415.728.729.115.226.152.45.167.641.015.174.015.379.015.589v.72c0 .21 0 .415-.015.588-.015.19-.052.416-.167.642-.16.313-.415.568-.728.728a1.685 1.685 0 0 1-.642.168 7.65 7.65 0 0 1-.589.014h-.72c-.21 0-.414 0-.588-.014a1.685 1.685 0 0 1-.641-.168 1.667 1.667 0 0 1-.729-.728 1.685 1.685 0 0 1-.167-.642c-.014-.173-.014-.378-.014-.588V6.307c0-.21 0-.415.014-.589.015-.19.052-.415.167-.641.16-.314.415-.569.729-.729.226-.115.45-.151.641-.167a7.65 7.65 0 0 1 .589-.014Zm-.464 1.677a6.821 6.821 0 0 0-.01.49V7a6.82 6.82 0 0 0 .01.49l.01.002c.098.008.233.008.48.008H7a6.83 6.83 0 0 0 .49-.01l.001-.01A6.82 6.82 0 0 0 7.5 7v-.667a6.821 6.821 0 0 0-.01-.49h-.01a6.823 6.823 0 0 0-.48-.01h-.667a6.823 6.823 0 0 0-.49.01Zm7.13-1.676h.72c.21 0 .415 0 .589.014.19.016.415.052.641.167.314.16.569.415.729.729.115.226.152.45.167.641.014.174.014.379.014.589v.72c0 .21 0 .415-.014.588-.015.19-.052.416-.167.642-.16.313-.415.568-.729.728-.226.115-.45.152-.641.168a7.65 7.65 0 0 1-.589.014h-.72c-.21 0-.415 0-.588-.014a1.685 1.685 0 0 1-.642-.168 1.667 1.667 0 0 1-.728-.728 1.684 1.684 0 0 1-.168-.642c-.014-.173-.014-.378-.014-.588v-.72c0-.21 0-.415.014-.589.016-.19.053-.415.168-.641.16-.314.415-.569.728-.729.226-.115.451-.151.642-.167a7.65 7.65 0 0 1 .588-.014Zm-.464 1.676v.011a6.845 6.845 0 0 0-.01.48V7a6.845 6.845 0 0 0 .01.49l.011.002c.098.008.233.008.48.008h.667a6.831 6.831 0 0 0 .49-.01l.001-.01c.008-.098.009-.233.009-.48v-.667a6.845 6.845 0 0 0-.01-.49h-.01a6.825 6.825 0 0 0-.48-.01H13a6.825 6.825 0 0 0-.49.01Zm-6.202 4.99h.72c.21 0 .415 0 .588.015.19.015.415.052.642.167.313.16.568.415.728.728.115.227.152.451.167.642.015.173.015.378.015.589v.72c0 .21 0 .415-.015.588-.015.19-.052.415-.167.642-.16.313-.415.568-.728.728a1.685 1.685 0 0 1-.642.167c-.173.014-.378.014-.588.014h-.72c-.21 0-.415 0-.589-.014a1.685 1.685 0 0 1-.641-.167 1.666 1.666 0 0 1-.729-.728 1.685 1.685 0 0 1-.167-.642c-.014-.173-.014-.378-.014-.589v-.72c0-.21 0-.415.014-.588.015-.19.052-.415.167-.642.16-.313.415-.568.729-.728.226-.115.45-.152.641-.167.174-.015.378-.014.589-.014Zm-.464 1.677-.001.01a6.821 6.821 0 0 0-.009.48v.667a6.823 6.823 0 0 0 .01.49l.01.001c.098.008.233.009.48.009H7a6.78 6.78 0 0 0 .49-.01l.001-.01c.008-.098.009-.233.009-.48V13a6.821 6.821 0 0 0-.01-.49l-.01-.001A6.865 6.865 0 0 0 7 12.5h-.667c-.247 0-.382 0-.48.009h-.01Zm7.13-1.676h.72c.21 0 .415 0 .589.014.19.015.415.052.641.167.314.16.569.415.729.728.115.227.152.451.167.642.014.173.014.378.014.589v.72c0 .21 0 .415-.014.588-.015.19-.052.415-.167.642-.16.313-.415.568-.729.728-.226.115-.45.152-.641.167-.174.014-.379.014-.589.014h-.72c-.21 0-.415 0-.588-.014a1.684 1.684 0 0 1-.642-.167 1.666 1.666 0 0 1-.728-.728 1.685 1.685 0 0 1-.168-.642c-.014-.173-.014-.378-.014-.589v-.72c0-.21 0-.415.014-.588.016-.19.053-.415.168-.642.16-.313.415-.568.728-.728.226-.115.451-.152.642-.167.173-.015.378-.014.588-.014Zm-.464 1.676a6.845 6.845 0 0 0-.01.49v.667a6.847 6.847 0 0 0 .01.49l.011.001c.098.008.233.009.48.009h.667a6.783 6.783 0 0 0 .49-.01 6.847 6.847 0 0 0 .01-.49V13a6.845 6.845 0 0 0-.01-.49l-.01-.001a6.867 6.867 0 0 0-.48-.009H13c-.247 0-.382 0-.48.009h-.01Z"
            fill="#989691"
          />
        </svg>,
        dashboardList.map((item, index) => {
          return getItem(
            item.dashboardName,
            `dashboard-${item.id}`,
            <HoverSetting
              menu={`dashboard-${item.id}`}
              key={index}
              name={item.dashboardName}
            />
          );
        }),
        `${
          location.pathname.split("/").length === 2 &&
          location.pathname.split("/")[1] === "dashboard" &&
          "color"
        }`
      ),
      getItem(
        <NavLink to="/cctv" onClick={onClickLink("cctv")}>
          CCTV
        </NavLink>,
        "cctv",
        <svg
          width={20}
          height={20}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.902 2.527c-.348-.026-.797-.027-1.458-.027-1.308 0-2.242 0-2.977.056-.726.055-1.188.16-1.561.335A4.167 4.167 0 0 0 2.89 4.906c-.174.373-.279.835-.334 1.561-.056.735-.056 1.67-.056 2.978v1.11c0 1.309 0 2.244.056 2.978.055.726.16 1.188.334 1.561a4.167 4.167 0 0 0 2.016 2.016c.373.174.835.279 1.561.334.735.056 1.67.056 2.977.056.66 0 1.11 0 1.458-.027.339-.025.514-.072.636-.129.355-.166.64-.451.806-.806.057-.122.103-.297.129-.636.026-.348.027-.797.027-1.457v-8.89c0-.66 0-1.109-.027-1.456-.026-.34-.072-.514-.13-.637a1.667 1.667 0 0 0-.805-.806c-.122-.057-.297-.103-.636-.129Zm.125-1.662c.434.033.835.103 1.215.28.71.332 1.281.903 1.612 1.613.178.38.248.78.28 1.215.033.418.033.93.033 1.55v1.253l3.704-2.47A.833.833 0 0 1 19.167 5v10a.833.833 0 0 1-1.296.694l-3.704-2.47v1.252c0 .621 0 1.133-.032 1.552-.033.434-.103.834-.28 1.214a3.333 3.333 0 0 1-1.613 1.613c-.38.177-.78.247-1.215.28-.418.032-.93.032-1.55.032H9.41c-1.266 0-2.264 0-3.069-.061-.82-.062-1.508-.191-2.14-.486A5.833 5.833 0 0 1 1.38 15.8c-.295-.632-.424-1.32-.486-2.14-.06-.805-.06-1.803-.06-3.07V9.41c0-1.266 0-2.264.06-3.069.062-.82.191-1.508.486-2.14A5.833 5.833 0 0 1 4.2 1.38c.632-.294 1.32-.423 2.14-.486.805-.06 1.803-.06 3.07-.06h.065c.62 0 1.133 0 1.551.031Zm3.14 10.356 3.333 2.222V6.557L14.167 8.78v2.442Z"
            fill="#989691"
          />
        </svg>,
        spaceList.map((item) => {
          return getItem(item.name, `cctv-${item.id}`);
        }),
        `${
          location.pathname.split("/").length === 2 &&
          location.pathname.split("/")[1] === "cctv" &&
          "color"
        }`
      ),
      getItem(
        <NavLink to="/sellManage" onClick={onClickLink("sellManage")}>
          매출 관리
        </NavLink>,
        "sellManage",
        <svg
          width={20}
          height={20}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.833 11.667a1.667 1.667 0 1 1-3.333 0h3.333Z"
            fill="#989691"
          />
          <path
            d="M9.62982 0.833252H10.3705C11.7393 0.833246 12.8176 0.833242 13.6853 0.904141C14.5706 0.976472 15.3099 1.12673 15.9818 1.46905C17.0794 2.02831 17.9718 2.9207 18.531 4.01831C18.8734 4.69014 19.0236 5.42944 19.0959 6.31473C19.1668 7.18249 19.1668 8.26078 19.1668 9.62955V10.3703C19.1668 11.7391 19.1668 12.8173 19.0959 13.6851C19.0236 14.5704 18.8734 15.3097 18.531 15.9815C17.9718 17.0791 17.0794 17.9715 15.9818 18.5308C15.3099 18.8731 14.5706 19.0234 13.6853 19.0957C12.8176 19.1666 11.7393 19.1666 10.3705 19.1666H9.6298C8.26102 19.1666 7.18273 19.1666 6.31498 19.0957C5.42968 19.0234 4.69039 18.8731 4.01855 18.5308C2.92094 17.9715 2.02855 17.0791 1.46929 15.9815C1.12697 15.3097 0.976716 14.5704 0.904385 13.6851C0.833486 12.8173 0.833491 11.739 0.833496 10.3703V9.62957C0.833491 8.26079 0.833486 7.1825 0.904385 6.31473C0.976716 5.42944 1.12697 4.69014 1.46929 4.01831C2.02855 2.9207 2.92094 2.02831 4.01855 1.46905C4.69039 1.12673 5.42968 0.976472 6.31498 0.904141C7.18274 0.833242 8.26103 0.833246 9.62982 0.833252ZM6.4507 2.56527C5.66847 2.62918 5.17293 2.75141 4.7752 2.95406C3.99119 3.35353 3.35377 3.99095 2.9543 4.77496C2.75165 5.17268 2.62943 5.66823 2.56552 6.45045C2.50081 7.24241 2.50016 8.2527 2.50016 9.66658V10.3333C2.50016 11.7471 2.50081 12.7574 2.56552 13.5494C2.62943 14.3316 2.75165 14.8272 2.9543 15.2249C3.35377 16.0089 3.99119 16.6463 4.7752 17.0458C5.17293 17.2484 5.66847 17.3707 6.4507 17.4346C7.24265 17.4993 8.25295 17.4999 9.66683 17.4999H10.3335C11.7474 17.4999 12.7577 17.4993 13.5496 17.4346C14.3319 17.3707 14.8274 17.2484 15.2251 17.0458C16.0091 16.6463 16.6465 16.0089 17.046 15.2249C17.2487 14.8272 17.3709 14.3316 17.4348 13.5494C17.4995 12.7574 17.5002 11.7471 17.5002 10.3333V9.66658C17.5002 8.2527 17.4995 7.24241 17.4348 6.45045C17.3709 5.66823 17.2487 5.17268 17.046 4.77496C16.6465 3.99095 16.0091 3.35353 15.2251 2.95406C14.8274 2.75141 14.3319 2.62918 13.5496 2.56527C12.7577 2.50057 11.7474 2.49992 10.3335 2.49992H9.66683C8.25295 2.49992 7.24265 2.50057 6.4507 2.56527ZM9.16683 5.09441V4.16659H10.8335V5.09441C12.1932 5.40534 13.3335 6.46064 13.3335 7.91658V8.33325H11.6668V7.91658C11.6668 7.52884 11.3747 7.0722 10.8335 6.83258V9.38166C11.379 9.56336 11.8935 9.78839 12.3095 10.0974C12.9281 10.5568 13.3335 11.2084 13.3335 12.0833C13.3335 13.5392 12.1932 14.5945 10.8335 14.9054V15.8333H9.16683V14.9054C7.80713 14.5945 6.66683 13.5392 6.66683 12.0833V11.6666H8.3335V12.0833C8.3335 12.471 8.6256 12.9276 9.16683 13.1673V10.6156C8.66035 10.4428 8.16353 10.2221 7.75028 9.92252C7.13866 9.47906 6.66683 8.82462 6.66683 7.91658C6.66683 6.46064 7.80713 5.40534 9.16683 5.09441ZM9.16683 6.83258C8.6256 7.0722 8.3335 7.52884 8.3335 7.91658C8.3335 8.15915 8.4307 8.35721 8.7286 8.57319C8.85092 8.66188 8.99742 8.74538 9.16683 8.82465V6.83258ZM10.8335 11.1642V13.1673C11.3747 12.9276 11.6668 12.471 11.6668 12.0833C11.6668 11.8075 11.5722 11.6258 11.3158 11.4354C11.1889 11.3411 11.0293 11.2515 10.8335 11.1642Z"
            fill="#989691"
          />
        </svg>,
        spaceList.map((item) => {
          return getItem(item.name, `sellManage-${item.id}`);
        }),
        `${
          location.pathname.split("/").length === 2 &&
          location.pathname.split("/")[1] === "sellManage" &&
          "color"
        }`
      ),
      getItem(
        <NavLink to="/space" onClick={onClickLink("space")}>
          장소 및 기기 관리
        </NavLink>,
        "space",
        <svg
          width={20}
          height={20}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.833 11.667a1.667 1.667 0 1 1-3.333 0h3.333Z"
            fill="#989691"
          />
          <path
            d="M15.981 1.47c-.672-.343-1.41-.493-2.296-.566-.868-.07-1.946-.07-3.315-.07h-.712c-.831 0-1.552 0-2.178.014-1.447.034-2.53.146-3.462.621a5.833 5.833 0 0 0-2.55 2.55c-.342.671-.492 1.41-.564 2.296C.833 7.183.833 8.26.833 9.63v.74c0 1.37 0 2.447.07 3.315.073.885.223 1.625.566 2.297a5.833 5.833 0 0 0 2.55 2.549c1.088.555 2.403.62 4.308.633.41.003.855.003 1.338.003h.705c1.369 0 2.447 0 3.315-.071.885-.073 1.624-.223 2.296-.565a5.833 5.833 0 0 0 2.55-2.55c.12-.235.217-.48.295-.74.34-1.123.34-2.588.34-4.74V9.63c0-1.37 0-2.447-.07-3.315-.073-.886-.223-1.625-.566-2.297a5.833 5.833 0 0 0-2.549-2.549ZM10.333 2.5c1.414 0 2.424 0 3.216.065.782.064 1.278.186 1.676.39a4.166 4.166 0 0 1 1.82 1.82c.203.398.325.893.39 1.676.064.791.065 1.802.065 3.216v.666c0 1.871-.006 3.014-.136 3.834H5v1.666h2.5v1.652c-1.412-.036-2.15-.146-2.725-.44a4.166 4.166 0 0 1-1.821-1.82c-.203-.398-.325-.893-.389-1.676-.065-.791-.065-1.802-.065-3.216v-.666c0-1.414 0-2.425.065-3.216.064-.783.186-1.278.389-1.676a4.167 4.167 0 0 1 1.82-1.82c.567-.29 1.308-.407 2.745-.44.253-.007.524-.01.814-.012v2.245L5.14 9.538a.833.833 0 0 0 .693 1.295H12.5a.833.833 0 0 0 .693-1.295L10 4.748V2.5h.333ZM9.166 15.833h7.5c-.38.507-.872.923-1.441 1.213-.398.203-.894.325-1.676.389-.792.064-1.802.065-3.216.065H9.166v-1.667ZM7.39 9.167l1.776-2.665 1.777 2.665H7.39Z"
            fill="#989691"
          />
        </svg>,
        spaceList.map((item, index) => {
          return getItem(
            item.name,
            `space-${item.id}`,
            <HoverSetting
              menu={`space-${item.id}`}
              key={index}
              name={item.name}
            />
          );
        }),
        `${
          location.pathname.split("/").length === 2 &&
          location.pathname.split("/")[1] === "space" &&
          "color"
        }`
      ),
      getItem(
        <NavLink to="/history" onClick={onClickLink("history")}>
          히스토리
        </NavLink>,
        "history",
        <svg
          width={20}
          height={20}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.63.833h.74c1.369 0 2.447 0 3.315.071.885.073 1.625.223 2.296.565a5.834 5.834 0 0 1 2.55 2.55c.342.671.492 1.41.565 2.296.07.868.07 1.946.07 3.315v.74c0 1.37 0 2.448-.07 3.315-.073.886-.223 1.625-.565 2.297a5.833 5.833 0 0 1-2.55 2.549c-.235.12-.48.217-.74.296-.926.28-2.059.326-3.569.337-.409.003-.854.003-1.337.003h-.67c-.483 0-.928 0-1.338-.003-1.51-.01-2.642-.056-3.569-.337a4.476 4.476 0 0 1-.74-.296 5.833 5.833 0 0 1-2.549-2.55c-.342-.671-.493-1.41-.565-2.296-.07-.867-.07-1.946-.07-3.315v-.74c0-1.369 0-2.447.07-3.315.072-.885.223-1.625.565-2.296a5.833 5.833 0 0 1 2.55-2.55c.671-.342 1.41-.492 2.296-.565.867-.07 1.946-.07 3.315-.07ZM6.45 2.566c-.782.063-1.277.186-1.675.388a4.167 4.167 0 0 0-1.82 1.821c-.204.398-.326.893-.39 1.676-.064.792-.065 1.802-.065 3.216v.667c0 1.413 0 2.424.065 3.216.064.782.186 1.277.39 1.675.29.57.705 1.062 1.212 1.442v-5.834c0-.46.373-.833.833-.833h2.5V5c0-.46.373-.833.833-.833h3.334c.46 0 .833.373.833.833v7.5H15c.46 0 .833.373.833.834v3.333c.507-.38.923-.872 1.213-1.442.202-.398.325-.893.389-1.675.064-.792.065-1.803.065-3.216v-.667c0-1.414 0-2.424-.065-3.216-.064-.783-.187-1.278-.39-1.676a4.167 4.167 0 0 0-1.82-1.82c-.398-.203-.893-.326-1.676-.39-.792-.064-1.802-.065-3.216-.065h-.666c-1.414 0-2.425 0-3.217.066Zm7.717 14.798v-3.197H12.5v3.318c.697-.017 1.23-.052 1.667-.121Zm-3.334.136V5.834H9.167V17.5h1.666ZM7.5 17.485v-5.818H5.833v5.697c.436.069.97.104 1.667.121Z"
            fill="#989691"
          />
        </svg>,
        undefined,
        `${
          location.pathname.split("/").length === 2 &&
          location.pathname.split("/")[1] === "history" &&
          "color"
        }`
      ),
      getItem(
        <NavLink to="/recipe" onClick={onClickLink("recipe")}>
          자동화
        </NavLink>,
        "recipe",
        <svg
          width={20}
          height={20}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.63.833h.74c1.369 0 2.447 0 3.315.071.885.073 1.625.223 2.296.565a5.834 5.834 0 0 1 2.55 2.55c.342.671.492 1.41.565 2.296.07.868.07 1.946.07 3.315v2.87a.833.833 0 0 1-.244.59l-5.833 5.833a.834.834 0 0 1-.59.244H9.63c-1.368 0-2.447 0-3.314-.071-.886-.072-1.625-.223-2.297-.565a5.833 5.833 0 0 1-2.549-2.55c-.342-.671-.493-1.41-.565-2.296-.07-.867-.07-1.946-.07-3.315v-.74c0-1.369 0-2.447.07-3.315.072-.885.223-1.625.565-2.296a5.833 5.833 0 0 1 2.55-2.55c.671-.342 1.41-.492 2.296-.565.867-.07 1.946-.07 3.315-.07ZM6.45 2.566c-.782.063-1.277.186-1.675.388a4.167 4.167 0 0 0-1.82 1.821c-.204.398-.326.893-.39 1.676-.064.792-.065 1.802-.065 3.216v.667c0 1.413 0 2.424.065 3.216.064.782.186 1.277.39 1.675a4.167 4.167 0 0 0 1.82 1.821c.398.203.893.325 1.675.389.792.065 1.803.065 3.217.065h2v-2.365c0-.44 0-.819.025-1.13.027-.33.086-.658.247-.973a2.5 2.5 0 0 1 1.093-1.093c.315-.16.643-.22.972-.247.312-.025.691-.025 1.13-.025H17.5v-2c0-1.414 0-2.424-.065-3.216-.064-.783-.187-1.278-.39-1.676a4.167 4.167 0 0 0-1.82-1.82c-.398-.203-.893-.326-1.676-.39-.792-.064-1.802-.065-3.216-.065h-.666c-1.414 0-2.425 0-3.217.066Zm9.871 10.768h-1.154c-.48 0-.791 0-1.027.02-.227.018-.31.05-.352.07a.833.833 0 0 0-.364.365c-.02.04-.052.125-.07.351-.02.236-.02.546-.02 1.027v1.155l2.987-2.988Zm-3.404-7.5a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5ZM10 7.083a2.917 2.917 0 1 1 1.529 2.566l-1.88 1.88a2.917 2.917 0 1 1-1.179-1.178l1.88-1.88A2.904 2.904 0 0 1 10 7.083Zm-2.917 4.583a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Z"
            fill="#989691"
          />
        </svg>,
        undefined,
        `${
          location.pathname.split("/").length === 2 &&
          location.pathname.split("/")[1] === "recipe" &&
          "color"
        }`
      ),
      getItem(
        "설정",
        "setting",
        <svg
          width={20}
          height={20}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="m9.457 2.5-.3 1.82a.833.833 0 0 1-.789.697c-.518.021-.823.068-1.064.146a3.33 3.33 0 0 0-.127.044.833.833 0 0 1-.774-.103L4.695 3.883l-.769.769L5.13 6.333c.164.23.201.526.099.788-.024.06-.045.12-.065.182-.078.24-.125.546-.146 1.064a.833.833 0 0 1-.697.788l-1.82.301v1.087l1.82.301c.39.065.68.394.697.788.021.519.068.824.146 1.064.02.062.041.123.065.182a.833.833 0 0 1-.099.788l-1.204 1.682.769.768 1.708-1.22c.225-.16.514-.2.774-.104.042.016.084.03.127.044.24.079.546.125 1.064.146a.833.833 0 0 1 .788.697l.301 1.82h1.087l.301-1.82a.833.833 0 0 1 .788-.697c.519-.021.824-.067 1.064-.146a3.24 3.24 0 0 0 .128-.044.833.833 0 0 1 .774.104l1.707 1.22.77-.768-1.205-1.682a.833.833 0 0 1-.098-.788c.023-.06.045-.12.065-.182.078-.24.124-.545.145-1.064a.833.833 0 0 1 .697-.788l1.82-.301V9.456l-1.82-.3a.833.833 0 0 1-.697-.789c-.021-.518-.067-.823-.146-1.064a3.38 3.38 0 0 0-.064-.182.833.833 0 0 1 .098-.788l1.204-1.681-.769-.77L13.6 5.105a.833.833 0 0 1-.774.103 3.36 3.36 0 0 0-.128-.044c-.24-.078-.545-.125-1.064-.146a.833.833 0 0 1-.788-.697l-.301-1.82H9.457ZM8.751.833h2.5c.407 0 .755.295.822.697l.31 1.873c.203.024.396.057.583.104l1.946-1.39a.833.833 0 0 1 1.074.088l1.767 1.768a.833.833 0 0 1 .089 1.074l-1.369 1.911c.058.211.097.429.125.66l1.872.31a.833.833 0 0 1 .697.822v2.5a.833.833 0 0 1-.697.822l-1.872.31a4.566 4.566 0 0 1-.125.659l1.369 1.911c.237.332.2.786-.089 1.074l-1.767 1.768a.833.833 0 0 1-1.074.089l-1.946-1.39c-.187.046-.38.08-.583.104l-.31 1.872a.833.833 0 0 1-.822.697h-2.5a.833.833 0 0 1-.823-.697l-.31-1.872a4.712 4.712 0 0 1-.582-.105l-1.947 1.391a.833.833 0 0 1-1.073-.089l-1.768-1.767a.833.833 0 0 1-.088-1.075l1.368-1.911a4.573 4.573 0 0 1-.124-.66l-1.873-.31a.833.833 0 0 1-.697-.821v-2.5c0-.408.295-.756.697-.822l1.873-.31c.027-.231.067-.449.124-.66L2.16 5.048a.833.833 0 0 1 .088-1.075l1.768-1.768a.833.833 0 0 1 1.073-.089l1.947 1.391c.186-.047.38-.08.583-.104l.31-1.873A.833.833 0 0 1 8.75.833Zm1.223 5.833h.053c.366 0 .681 0 .942.018.275.019.55.06.822.173a2.5 2.5 0 0 1 1.353 1.353c.112.271.153.546.172.821.018.261.018.576.018.942v.053c0 .366 0 .681-.018.942-.019.275-.06.55-.172.822a2.5 2.5 0 0 1-1.353 1.353 2.556 2.556 0 0 1-.822.172c-.26.018-.576.018-.942.018h-.053c-.366 0-.681 0-.942-.018a2.556 2.556 0 0 1-.821-.172 2.5 2.5 0 0 1-1.353-1.353 2.557 2.557 0 0 1-.173-.822c-.018-.26-.018-.576-.018-.942v-.053c0-.366 0-.68.018-.942.019-.275.06-.55.173-.821A2.5 2.5 0 0 1 8.21 6.857c.271-.113.546-.154.821-.173.261-.018.576-.018.942-.018Zm-.829 1.68a.967.967 0 0 0-.297.05.833.833 0 0 0-.45.451.966.966 0 0 0-.05.297c-.014.198-.014.456-.014.856 0 .4 0 .657.014.855.013.19.035.262.05.297a.833.833 0 0 0 .45.45.963.963 0 0 0 .297.05c.198.014.456.014.856.014.4 0 .657 0 .855-.014a.964.964 0 0 0 .297-.05.833.833 0 0 0 .45-.45.97.97 0 0 0 .05-.297c.014-.198.014-.456.014-.855 0-.4 0-.658-.014-.856a.97.97 0 0 0-.05-.297.833.833 0 0 0-.45-.45.968.968 0 0 0-.297-.05A14.1 14.1 0 0 0 10 8.333c-.4 0-.658 0-.856.014Z"
            fill="#989691"
          />
        </svg>,
        undefined,
        `${
          location.pathname.split("/").length === 2 &&
          location.pathname.split("/")[1] === "setting" &&
          "color"
        }`
      ),
    ];
  }, [
    dashboardList,
    spaceList,
    menu,
    dashboardId,
    dispatch,
    dashboardRegisterSuccess,
    current,
    dashboardInsertId,
    dashboardName,
    dashboardRenameSuccess,
    dashboardDeleteSuccess,
    updateSpaceSuccess,
    createSpaceSuccess,
    spaceTabList,
    updateSpaceId,
    updateName,
    removeSpaceSuccess,
    location.pathname,
  ]);

  return (
    <Menu
      mode="inline"
      style={{ height: "100vh" }}
      items={items}
      selectedKeys={[current]}
      onSelect={onSelectMenu}
      openKeys={[currentMenu]}
      onOpenChange={onOpenChange}
      disabled={disabledTimer}
      triggerSubMenuAction={"click"}
    />
  );
}

export default NavigationMenus;
